import React from 'react';
import classNames from 'classnames';
import { TwainGameId } from 'betgames-contract-js';
import {
    IParticipantsPair,
    ITournamentMatchShotData,
    IScores,
    IParticipant,
} from 'twain-sport-fe-mono/interfaces';
import { RngParticipantIcon } from 'twain-sport-fe-mono/icons';
import { scrollToTarget, ScrollDirection } from 'twain-sport-fe-mono/utils';
import { ThermometerRow } from './ThermometerRow';
import { ColorStyles } from './enums/ColorStyles';
import { Avatar } from '../Avatar';
import { ParticipantInfo } from './ParticipantInfo';
import classes from './Thermometer.module.scss';

interface ILinkProps {
    code: IParticipant['code'];
    gameId: TwainGameId;
}

interface IProps {
    participants: IParticipantsPair;
    scores: IScores<ITournamentMatchShotData[]>;
    historical?: boolean;
    colorStyle?: ColorStyles;
    className?: string;
    hideAvatars?: boolean;
    gameId: TwainGameId;
    Link?: React.FC<React.PropsWithChildren<ILinkProps>>;
    isRng?: boolean;
}

const defaultProps: Partial<IProps> = {
    colorStyle: ColorStyles.DEFAULT,
};

const RNG_SHOTS_COUNT = 20;

export const Thermometer: React.FC<IProps> = (props) => {
    const thermometersDivRef = React.useRef<HTMLDivElement>();
    const scoresDivRef = React.useRef<HTMLDivElement>();
    const scores1 = props.scores[props.participants.left.code] ?? [];
    const scores2 = props.scores[props.participants.right.code] ?? [];
    const maxParticipantScoresCount = Math.max(scores1.length, scores2.length);

    const scoresList = React.useMemo(
        () =>
            Array(RNG_SHOTS_COUNT)
                .fill(null)
                .map((_, index) => ({
                    order: index + 1,
                    points: 1,
                })),
        [],
    );

    const scoresItemsCount = props.isRng
        ? scoresList.length
        : Math.max(RNG_SHOTS_COUNT, scores1.length, scores2.length);

    React.useEffect(() => {
        let cancelAnimationFrame = (): void => {};

        if (
            thermometersDivRef.current &&
            scoresDivRef.current?.children[maxParticipantScoresCount]
        ) {
            cancelAnimationFrame = scrollToTarget(
                thermometersDivRef.current,
                scoresDivRef.current.children[maxParticipantScoresCount] as HTMLElement,
                ScrollDirection.HORIZONTAL,
                true,
                95, // keep target visible at 95% position in scrollbar container
                true,
            );
        }

        return cancelAnimationFrame;
    }, [maxParticipantScoresCount]);

    const renderAvatar = React.useCallback(
        (element: React.ReactNode, code: IParticipant['code']) => {
            if (props.isRng) {
                const court = props.participants.left.code === code ? 'left' : 'right';
                return <RngParticipantIcon court={court} />;
            }

            if (!props.Link) {
                return element;
            }

            const ParticipantLink = props.Link;
            return (
                <ParticipantLink code={code} gameId={props.gameId}>
                    {element}
                </ParticipantLink>
            );
        },
        [props.Link, props.gameId, props.isRng],
    );

    return (
        <div
            className={classNames(classes.container, props.className, {
                [classes.withAvatars]: !props.hideAvatars,
                [classes.rng]: props.isRng,
            })}
        >
            {!props.hideAvatars && (
                <div className={classes.avatars}>
                    {renderAvatar(
                        <Avatar
                            className={classNames(classes.avatar, classes.left)}
                            src={props.participants.left.avatarPictureUrl}
                            size="tiny"
                        />,
                        props.participants.left.code,
                    )}
                    {renderAvatar(
                        <Avatar
                            className={classNames(classes.avatar, classes.right)}
                            src={props.participants.right.avatarPictureUrl}
                            size="tiny"
                        />,
                        props.participants.right.code,
                    )}
                </div>
            )}
            <div className={classes.case}>
                {!props.isRng && <ParticipantInfo participant={props.participants.left} />}
                <div
                    className={classes.thermometers}
                    style={{ '--cell-count': `${scoresItemsCount}` } as React.CSSProperties}
                    ref={thermometersDivRef}
                >
                    <ThermometerRow
                        scores={scores1}
                        length={scoresItemsCount}
                        side="left"
                        historical={props.historical}
                        colorStyle={props.colorStyle}
                    />
                    <div className={classes.scores} ref={scoresDivRef}>
                        {scoresList.slice(0, scoresItemsCount).map((score, index) => (
                            <span key={`${score.order}_${index}`} className={classes.scoreCell}>
                                {score.points}
                            </span>
                        ))}
                    </div>
                    <ThermometerRow
                        scores={scores2}
                        length={scoresItemsCount}
                        side="right"
                        historical={props.historical}
                        colorStyle={props.colorStyle}
                    />
                </div>
                {!props.isRng && <ParticipantInfo participant={props.participants.right} />}
            </div>
        </div>
    );
};

Thermometer.defaultProps = defaultProps;
